<template>
	<teleport to="#app">
		<BaseDialog
			:isOpen="isVisible"
			@close="close"
			@runAfterLeave="runAfterLeave"
		>
			<template #default>
				<!-- Modal Backdrop with fade animation -->
				<Transition
					enter-active-class="transition-all duration-300 ease-out"
					enter-from-class="opacity-0"
					enter-to-class="opacity-100"
					leave-active-class="transition-all duration-200 ease-in"
					leave-from-class="opacity-100"
					leave-to-class="opacity-0"
				>
					<div
						v-if="isVisible"
						class="fixed inset-0 flex items-center justify-center z-50 bg-black/20 antialiased backdrop-blur-sm"
						@click="close"
					>
						<Transition
							enter-active-class="transition-all duration-400 ease-out"
							enter-from-class="opacity-0 scale-95 translate-y-8"
							enter-to-class="opacity-100 scale-100 translate-y-0"
							leave-active-class="transition-all duration-250 ease-in"
							leave-from-class="opacity-100 scale-100 translate-y-0"
							leave-to-class="opacity-0 scale-95 translate-y-4"
							appear
						>
							<div
								v-if="isVisible"
								class="relative flex w-full max-w-lg sm:max-w-2xl lg:max-w-4xl max-h-[90vh] flex-col bg-white rounded-2xl border border-gray-200/50 shadow-2xl transform-gpu overflow-hidden"
								@click.stop
							>
								<!-- Close Button -->
								<button
									@click="close"
									class="cursor-pointer absolute top-3 right-3 z-10 p-2 hover:bg-slate-50 rounded-xl transition-all duration-200 group hover:scale-105 active:scale-95"
								>
									<XMarkIcon
										class="w-5 h-5 sm:w-6 sm:h-6 text-slate-500 group-hover:text-slate-700 transition-all duration-200"
									/>
								</button>

								<!-- Content Container -->
								<div class="flex flex-col h-full overflow-y-auto">
									<Transition
										enter-active-class="transition-all duration-500 ease-out delay-100"
										enter-from-class="opacity-0 translate-y-[20px]"
										enter-to-class="opacity-100 translate-y-0"
										appear
									>
										<section
											v-if="isVisible"
											class="flex flex-col items-center justify-center p-6 sm:p-8 lg:p-10"
										>
											<!-- Header -->
											<Transition
												enter-active-class="transition-all duration-400 ease-out delay-200"
												enter-from-class="opacity-0 translate-y-[-15px]"
												enter-to-class="opacity-100 translate-y-0"
												appear
											>
												<div v-if="isVisible" class="text-center mb-2 sm:mb-4">
													<h1
														class="text-lg sm:text-xl lg:text-2xl font-bold text-slate-950 mb-2 capitalize"
													>
														{{ getTranslations("getting started") }}
													</h1>
												</div>
											</Transition>

											<Transition
												enter-active-class="transition-all duration-600 ease-out delay-300"
												enter-from-class="opacity-0 scale-95"
												enter-to-class="opacity-100 scale-100"
												appear
											>
												<div class="flex flex-col h-full overflow-y-auto rounded-2xl">
													<section
														v-if="isVisible"
														class="flex flex-col items-center justify-center p-0 relative"
													>
														<!-- Video Container -->
														<div class="relative w-full bg-black rounded-xl overflow-hidden">
															<video
																ref="videoElement"
																class="w-full h-full object-cover"
																@loadedmetadata="onLoadedMetadata"
																@timeupdate="onTimeUpdate"
																@play="onPlay"
																@pause="onPause"
																@ended="onEnded"
															>
																<source :src="videoSrc" type="video/mp4" />
																Your browser does not support the video tag.
															</video>
															
															<!-- Custom Video Controls -->
															<div 
																class="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4 opacity-0 transition-opacity duration-300 hover:opacity-100"
																:class="{ 'opacity-100': showControls || !isPlaying }"
																@mouseenter="showControls = true"
																@mouseleave="showControls = false"
															>
																<!-- Progress Bar -->
																<div class="mb-3">
																	<div class="relative h-1 bg-white/30 rounded-full cursor-pointer" @click="seekTo">
																		<div 
																			class="absolute top-0 left-0 h-full bg-blue-500 rounded-full transition-all duration-100"
																			:style="{ width: progressPercentage + '%' }"
																		></div>
																		<div 
																			class="absolute top-1/2 transform -translate-y-1/2 w-3 h-3 bg-blue-500 rounded-full cursor-pointer transition-all duration-100 hover:scale-125"
																			:style="{ left: progressPercentage + '%' }"
																		></div>
																	</div>
																</div>
																
																<!-- Control Buttons -->
																<div class="flex items-center justify-between">
																	<div class="flex items-center space-x-3">
																		<!-- Play/Pause Button -->
																		<button
																			@click="togglePlay"
																			class="flex items-center justify-center w-10 h-10 bg-white/20 hover:bg-white/30 rounded-full transition-all duration-200 hover:scale-110"
																		>
																			<PlayIcon v-if="!isPlaying" class="w-5 h-5 text-white ml-0.5" />
																			<PauseIcon v-else class="w-5 h-5 text-white" />
																		</button>
																		
																		<!-- Volume Control -->
																		<div class="flex items-center space-x-2">
																			<button
																				@click="toggleMute"
																				class="flex items-center justify-center w-8 h-8 hover:bg-white/20 rounded-full transition-all duration-200"
																			>
																				<SpeakerWaveIcon v-if="!isMuted && volume > 0" class="w-4 h-4 text-white" />
																				<SpeakerXMarkIcon v-else class="w-4 h-4 text-white" />
																			</button>
																			<input
																				type="range"
																				min="0"
																				max="1"
																				step="0.1"
																				:value="volume"
																				@input="setVolume"
																				class="w-16 h-1 bg-white/30 rounded-lg appearance-none cursor-pointer slider"
																			/>
																		</div>
																	</div>
																	
																	<!-- Time Display -->
																	<div class="text-white text-sm font-medium">
																		{{ formatTime(currentTime) }} / {{ formatTime(duration) }}
																	</div>
																	
																	<div class="flex items-center space-x-2">
																		<!-- Fullscreen Button -->
																		<button
																			@click="toggleFullscreen"
																			class="flex items-center justify-center w-8 h-8 hover:bg-white/20 rounded-full transition-all duration-200"
																		>
																			<ArrowsPointingOutIcon class="w-4 h-4 text-white" />
																		</button>
																	</div>
																</div>
															</div>
														</div>
													</section>
												</div>
											</Transition>
										</section>
									</Transition>
								</div>
							</div>
						</Transition>
					</div>
				</Transition>
			</template>
		</BaseDialog>
	</teleport>
</template>

<script src="./HelpModal"></script>

<style scoped>
.slider::-webkit-slider-thumb {
	appearance: none;
	width: 12px;
	height: 12px;
	border-radius: 50%;
	background: #3b82f6;
	cursor: pointer;
	transition: all 0.2s ease;
}

.slider::-webkit-slider-thumb:hover {
	background: #2563eb;
	transform: scale(1.2);
}

.slider::-moz-range-thumb {
	width: 12px;
	height: 12px;
	border-radius: 50%;
	background: #3b82f6;
	cursor: pointer;
	border: none;
	transition: all 0.2s ease;
}

.slider::-moz-range-thumb:hover {
	background: #2563eb;
	transform: scale(1.2);
}

.slider::-moz-range-track {
	background: rgba(255, 255, 255, 0.3);
	height: 4px;
	border-radius: 2px;
	border: none;
}
</style>
